import React from 'react';
import { useHistory } from 'react-router';
import PropTypes from 'prop-types';
import { Box, Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Trans, useTranslation } from 'react-i18next';
import ClaimNumberLink from '../../../components/claim-number-link';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3)
  },
  authNumber: {
    fontWeight: 500
  },
  actionMessage: {
    marginTop: theme.spacing(3)
  },
  viewClaimBtn: {
    '&:hover': {
      color: theme.palette.common.white
    }
  },
  denialDetails: {
    padding: '0px 24px 16px'
  },
  textError: {
    color: '#FF0000'
  },
  ClaimNumberLink: {
    marginTop: theme.spacing(2.5),
    '& a': {
      textDecoration: 'none',
      color: theme.palette.secondary.main,
      fontWeight: 'bold',
      fontSize: '18px',
      '&:hover': {
        textDecoration: 'underline'
      }
    }
  }
}));

const ClaimNumberHeader = ({ claims }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const claimDetailPath = '/glass/view-claim';
  const claim = claims.claimNumbers[0];
  const searchParam = `?claimId=${claim.claimId}`;
  let dealerCode = '';
  let trackingNumber = '';

  try {
    dealerCode = claim.dealerInfo.dealerCode;
    trackingNumber = claim.orderInfo.trackingNumber;
  } catch (error) {
    console.error('Error extracting dealerCode or shipperNumber:', error);
  }

  return (
    <>
      <Box mt={2}>
        <Typography
          variant="h4"
          color="textPrimary"
          align="center"
          style={{ fontStyle: 'italic', fontWeight: 'bold' }}
        >
          {t('glassClaimSummary.thankYou')}
        </Typography>
      </Box>
      <Box mt={2.5}>
        <Typography
          variant="h4"
          color="textPrimary"
          align="center"
          style={{ fontWeight: 'normal' }}
        >
          {t('glassClaimSummary.requestHeading')}
          <ClaimNumberLink
            key={claim.claimId}
            claimNumbers={claims.claimNumbers}
            className={classes.ClaimNumberLink}
            pathname={claimDetailPath}
            searchParams={searchParam}
            state={{
              fromGlassSearchView: true,
              dealerCode,
              trackingNumber,
              isNewClaim: true
            }}
          />{' '}
        </Typography>
      </Box>
    </>
  );
};

ClaimNumberHeader.propTypes = {
  claims: PropTypes.shape({
    coverageName: PropTypes.string,
    claimNumbers: PropTypes.arrayOf(
      PropTypes.shape({
        claimNumber: PropTypes.string,
        claimId: PropTypes.number,
        qoreClaimId: PropTypes.number,
        dealerInfo: PropTypes.shape({
          dealerCode: PropTypes.string
        }),
        orderInfo: PropTypes.shape({
          trackingNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        }),
        authorizationCode: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.object
        ])
      })
    )
  }).isRequired
};

const ClaimActionButtons = ({ claim }) => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();
  const claimDetailPath = '/glass/view-claim';
  const searchParam = `?claimId=${claim.claimId}`;
  let dealerCode = '';
  let trackingNumber = '';

  try {
    dealerCode = claim.dealerInfo.dealerCode;
    trackingNumber = claim.orderInfo.trackingNumber;
  } catch (error) {
    console.error('Error extracting dealerCode or shipperNumber:', error);
  }

  const navigateToClaim = startTab => {
    history.push({
      pathname: claimDetailPath,
      search: searchParam,
      state: {
        fromGlassSearchView: true,
        dealerCode,
        trackingNumber,
        startTab,
        isNewClaim: true
      }
    });
  };

  return (
    <Box mt={4} component="footer" display="flex" justifyContent="center">
      <Button
        variant="contained"
        color="secondary"
        onClick={() => navigateToClaim('overview')}
        className={classes.viewClaimBtn}
      >
        {t('glassClaimSummary.viewClaimButton')}
      </Button>
      <Button
        variant="outlined"
        color="secondary"
        style={{ marginLeft: 8 }}
        onClick={() => navigateToClaim('documents')}
      >
        {t('glassClaimSummary.uploadPhotosButton')}
      </Button>
    </Box>
  );
};

ClaimActionButtons.propTypes = {
  claim: PropTypes.shape({
    claimId: PropTypes.number,
    dealerInfo: PropTypes.shape({
      dealerCode: PropTypes.string
    }),
    orderInfo: PropTypes.shape({
      trackingNumber: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
      ])
    })
  }).isRequired
};

const UnauthorizedClaimSummary = ({ claims }) => {
  const { t } = useTranslation();
  const claim = claims.claimNumbers[0];

  return (
    <>
      <ClaimNumberHeader claims={claims} />
      <Box mt={3}>
        <Typography variant="subtitle1" color="textPrimary" align="center">
          {t('glassClaimSummary.reviewMessage')}
        </Typography>
      </Box>
      <Box mt={3}>
        <Typography variant="subtitle1" color="textPrimary" align="center">
          <Trans
            i18nKey="glassClaimSummary.viewClaimPrompt"
            components={{ bold: <b /> }}
          />
        </Typography>
      </Box>
      <Box mt={1}>
        <Typography variant="subtitle1" color="textPrimary" align="center">
          <Trans
            i18nKey="glassClaimSummary.uploadPhotosPrompt"
            components={{ bold: <b /> }}
          />
        </Typography>
      </Box>
      <Box mt={1}>
        <Typography variant="subtitle1" color="textPrimary" align="center">
          <Trans
            i18nKey="glassClaimSummary.questionsPrompt"
            components={{ bold: <b /> }}
          />
        </Typography>
      </Box>
      <Box mt={1}>
        <Typography variant="subtitle1" color="textPrimary" align="center">
          {t('glassClaimSummary.callToAction')}
        </Typography>
      </Box>
      <ClaimActionButtons claim={claim} />
    </>
  );
};

UnauthorizedClaimSummary.propTypes = {
  claims: PropTypes.shape({
    claimNumbers: PropTypes.arrayOf(
      PropTypes.shape({
        claimNumber: PropTypes.string,
        claimId: PropTypes.number,
        qoreClaimId: PropTypes.number,
        dealerInfo: PropTypes.shape({
          dealerCode: PropTypes.string
        }),
        orderInfo: PropTypes.shape({
          trackingNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        }),
        authorizationCode: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.object
        ]),
        isDenied: PropTypes.bool,
        deniedCoverageDetails: PropTypes.arrayOf(
          PropTypes.shape({
            coverageName: PropTypes.string,
            coverageNumber: PropTypes.number,
            deniedRules: PropTypes.arrayOf(
              PropTypes.shape({
                ruleName: PropTypes.string,
                message: PropTypes.string
              })
            )
          })
        )
      })
    )
  }).isRequired
};

const ClaimCreationSummary = ({ claims }) => (
  <UnauthorizedClaimSummary claims={claims} />
);

ClaimCreationSummary.propTypes = {
  claims: PropTypes.shape({
    claimNumbers: PropTypes.array
  }).isRequired
};

export default ClaimCreationSummary;
